import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import IconButton from '@material-ui/core/IconButton';
import Modal from '@material-ui/core/Modal';
import CloseIcon from '@material-ui/icons/Close';
import cx from 'classnames';
import React, { useMemo, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { color } from 'styles/utils';
import { T } from '../text';

interface ClinicModalProps {
  isOpen: boolean;
  children: React.ReactNode;
  hideBackdrop?: boolean;
  disableBackdropClick?: boolean;
  title?: string;
  isCloseIcon?: boolean;
  width?: number;
  onClose?: () => void;
  onCancel?: (() => void) | null;
  onConfirm?: (() => void) | null;
  confirmDisabled?: boolean;
}

const ClinicModal = ({
  isOpen = true,
  children,
  hideBackdrop = false,
  disableBackdropClick = false,
  title = '',
  isCloseIcon = true,
  width = 580,
  onClose,
  onCancel = null,
  onConfirm = null,
  confirmDisabled = false,
}: ClinicModalProps) => {
  const [isButtonClick, setIsButtonClick] = useState<boolean>(false);
  const handleClose = () => {
    if (disableBackdropClick || !onClose) return;
    onClose();
  };

  return (
    <Styled.ClinicModal data-component-name="ClinicModal">
      <Modal
        aria-labelledby="clinic-modal-title"
        aria-describedby="clinic-modal-description"
        open={isOpen}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 250,
        }}
        hideBackdrop={hideBackdrop}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Fade
          in={isOpen}
          timeout={250}
          onExit={handleClose}
          style={{ width: width, borderRadius: 2, outline: 'none' }}
        >
          <div className={cx('clinicModal__container')}>
            {isCloseIcon && (
              <IconButton
                aria-label="close modal"
                className="clinicModal__close_btn"
                onClick={handleClose}
              >
                <CloseIcon className="clinicModal__close_icon" />
              </IconButton>
            )}
            <div className="clinicModal__wrapper">
              <div className="clinicModal__title_box">{title}</div>
              <div className="clinicModal__contents_box">{children}</div>
              {(!!onCancel || !!onConfirm) && (
                <div className="clinicModal__button_box">
                  {!!onCancel && (
                    <IconButton
                      className="clinicModal__button cancel"
                      onClick={() => {
                        setIsButtonClick(true);
                        onCancel();
                      }}
                    >
                      <T>GLOBAL_CANCEL</T>
                    </IconButton>
                  )}
                  {!!onConfirm && (
                    <IconButton
                      className="clinicModal__button confirm"
                      onClick={() => {
                        setIsButtonClick(true);
                        onConfirm();
                      }}
                      disabled={!!confirmDisabled}
                    >
                      <T>GLOBAL_OK</T>
                    </IconButton>
                  )}
                </div>
              )}
            </div>
          </div>
        </Fade>
      </Modal>
      {useMemo(
        () => (
          <Styled.ClinicModalGlobalStyle />
        ),
        [onCancel, onConfirm],
      )}
    </Styled.ClinicModal>
  );
};

const Styled = {
  ClinicModalGlobalStyle: createGlobalStyle<{}>`
  position: relative;
    .clinicModal__container {
      position: relative;
      .clinicModal__close_btn {  
        z-index: 1;
        position: absolute;
        top: 10px;
        right: 10px;
        padding: 5px;
      }
      .clinicModal__wrapper {
        width: 100%;
        height: 100%;
        border-radius: 20px;
        background-color: #fff;
        overflow: hidden;
        .clinicModal__title_box {
          width: 100%;
          height: 64px;
          padding: 20px;
          font-size: 20px;
          font-weight: 400;
          color: #222;
        }
        .clinicModal__contents_box {
          padding: 20px;
        }

        .clinicModal__button_box {
          padding: 0 20px 20px 20px;
          display: flex;
          justify-content: flex-end;
          align-items: center;
          column-gap: 10px;
          .clinicModal__button {
            width: 156px;
            height: 40px;
            border-radius: 20px;
            font-size: 14px;
            font-weight: 400;
            border: 1px solid #D1D1D1;
            background-color: #fff;
            color: #444;

            &.confirm {
              &.Mui-disabled {
                border: 1px solid #e1e1e7;
                background-color: #f9f9fb;
                color: #e1e1e7;
              }
            }
            
            &:hover {
              border: 1px solid #00A4E3;
              background-color: #DCECF3;
              color: #00A4E3;
            }

          }
        }
      }
    }
    &::-webkit-scrollbar-track {
      background-color: ${color.white};
    }
  `,
  ClinicModal: styled.div`
    position: relative;
  `,
};

export default React.memo(ClinicModal);
